// ADV-266 — Figma "KONTO | 04 Vergleich" (desktop 64223:12612 / mobile 64223:12717): breadcrumb back
// to the dashboard, "Nur Unterschiede anzeigen", and one card holding the comparison as a table —
// a column per car (photo, name, trim, remove), a row per feature, grouped in sections that fold.
// More cars than fit scroll sideways inside the card; the feature column stays in place.
import * as React from 'react';
import { Link } from 'react-router-dom';
import { isSameAcross } from './compareRows';
import breadcrumbChevron from './icons/breadcrumb-chevron.svg';
import sectionChevron from './icons/section-chevron.svg';
import removeIcon from './icons/remove.svg';
const ns = (key) => `ComparePage:cmp.${key}`;
const Cell = ({ cell }) => {
    switch (cell.kind) {
        case 'rate':
            return (React.createElement("span", { className: "cmp-rate" },
                React.createElement("span", { className: "cmp-rate-amount" }, cell.amount),
                React.createElement("span", { className: "cmp-rate-unit" }, cell.unit)));
        case 'rating':
            return React.createElement("span", { className: cell.good ? 'cmp-rating cmp-rating--good' : 'cmp-rating' }, cell.label);
        case 'co2':
            return React.createElement("span", { className: `cmp-co2 cmp-co2--${cell.letter.toLowerCase()}` }, cell.letter);
        case 'chips':
            return (React.createElement("ul", { className: "cmp-chips" }, cell.items.map((item, index) => React.createElement("li", { key: `${index}:${item}`, className: "cmp-chip" }, item))));
        case 'text':
            return React.createElement(React.Fragment, null, cell.text);
        default:
            return React.createElement("span", { "aria-hidden": "true" }, '—');
    }
};
// the kind of a row's values (rate, co2, chips…) sets its padding on mobile
const rowClass = (row) => {
    const filled = row.cells.find((cell) => cell.kind !== 'none');
    return [
        'cmp-row',
        filled && filled.kind !== 'text' ? `cmp-row--${filled.kind}` : '',
        row.small ? 'cmp-row--small' : '',
        row.desktopOnly ? 'cmp-row--desktop' : '',
    ].filter(Boolean).join(' ');
};
const cellClass = (cell) => {
    if (cell.kind === 'text' || cell.kind === 'none')
        return 'cmp-val cmp-val--text';
    return `cmp-val cmp-val--${cell.kind}`;
};
const CompareView = ({ t, cars, sections, loading, diffOnly, onToggleDiff, onRemove }) => {
    const [folded, setFolded] = React.useState({});
    const toggle = (key) => setFolded((state) => ({ ...state, [key]: !state[key] }));
    // "Nur Unterschiede anzeigen": only the rows where the cars differ
    const shown = sections
        .map((section) => ({ ...section, rows: diffOnly && cars.length > 1 ? section.rows.filter((row) => !isSameAcross(row)) : section.rows }))
        .filter((section) => section.rows.length > 0);
    const columns = cars.length + 1;
    return (React.createElement("div", { className: "cmp-page" },
        React.createElement("div", { className: "cmp-top" },
            React.createElement("nav", { className: "cmp-crumbs", "aria-label": t(ns('breadcrumbLabel')) },
                React.createElement(Link, { className: "cmp-crumb-link", to: "/account/dashboard" }, t(ns('dashboard'))),
                React.createElement("img", { className: "cmp-crumb-sep", src: breadcrumbChevron, alt: "", width: 14, height: 14 }),
                React.createElement("h1", { className: "cmp-crumb-current", "aria-current": "page" }, t(ns('title')))),
            React.createElement("div", { className: "cmp-toolbar" },
                React.createElement("button", { type: "button", className: "cmp-diff", "aria-pressed": diffOnly, onClick: onToggleDiff, disabled: loading || cars.length < 2 }, t(ns('differences'))))),
        loading ? (React.createElement("div", { className: "mv-loading" },
            React.createElement("span", { className: "auth-spinner" }))) : (React.createElement("section", { className: "cmp-card", "aria-label": t(ns('title')) },
            React.createElement("div", { className: "cmp-scroll" },
                React.createElement("table", { className: "cmp-table", style: { ['--cmp-cars']: cars.length } },
                    React.createElement("colgroup", null,
                        React.createElement("col", { className: "cmp-col-label" }),
                        cars.map((car) => React.createElement("col", { key: car.id }))),
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("td", { className: "cmp-spacer" },
                                React.createElement("span", { className: "cmp-count" }, t(ns('count'), { count: cars.length })),
                                React.createElement("span", { className: "cmp-count-sub" }, t(ns('inComparison')))),
                            cars.map((car) => (React.createElement("th", { key: car.id, scope: "col", className: "cmp-car" },
                                React.createElement(Link, { className: "cmp-car-link", to: `/search/vehicle/${car.id}` },
                                    React.createElement("img", { className: "cmp-car-photo", src: car.photo, alt: "", width: 170, height: 128, decoding: "async" }),
                                    React.createElement("span", { className: "cmp-car-name", translate: "no" }, car.name),
                                    car.subtitle ? React.createElement("span", { className: "cmp-car-sub", translate: "no" }, car.subtitle) : null),
                                onRemove ? (React.createElement("button", { type: "button", className: "cmp-remove", "aria-label": t(ns('remove'), { car: car.name }), onClick: () => onRemove(car.id) },
                                    React.createElement("img", { src: removeIcon, alt: "", width: 14, height: 14 }))) : null))))),
                    shown.map((section) => {
                        const open = !folded[section.key];
                        return (React.createElement("tbody", { key: section.key, className: "cmp-section" },
                            React.createElement("tr", null,
                                React.createElement("th", { scope: "rowgroup", colSpan: columns, className: "cmp-sec" },
                                    React.createElement("button", { type: "button", className: "cmp-sec-btn", "aria-expanded": open, onClick: () => toggle(section.key) },
                                        React.createElement("span", { className: "cmp-sec-title" }, section.title),
                                        React.createElement("img", { className: open ? 'cmp-sec-chevron' : 'cmp-sec-chevron cmp-sec-chevron--down', src: sectionChevron, alt: "", width: 18, height: 18 })))),
                            open ? section.rows.map((row) => (React.createElement("tr", { key: row.key, className: rowClass(row) },
                                React.createElement("th", { scope: "row", className: "cmp-lab" },
                                    React.createElement("span", { className: "cmp-lab-inner" },
                                        React.createElement("img", { className: "cmp-lab-icon", src: row.icon, alt: "", width: 16, height: 16 }),
                                        React.createElement("span", { className: "cmp-lab-text" }, row.label))),
                                row.cells.map((cell, index) => (React.createElement("td", { key: cars[index] ? cars[index].id : index, className: cellClass(cell) },
                                    React.createElement(Cell, { cell: cell }))))))) : null));
                    }))),
            diffOnly && !shown.length ? React.createElement("p", { className: "cmp-empty", role: "status" }, t(ns('noDifferences'))) : null))));
};
export default CompareView;
